import { useState, useEffect, useRef, useCallback } from 'react';
import { useTimer } from '../contexts/TimerContext';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Clock, Coffee, Trash2, Check, AlertTriangle, Pause } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Global Idle Alert Dialog — uses TimerContext for idle detection.
 * Idle detection logic lives in TimerContext (activity tracking, threshold checks).
 * This component only handles the UI and user actions.
 */
const IdleAlertDialog = () => {
  const {
    isRunning, isPaused,
    showIdleDialog, idleMinutes,
    keepIdleTime, discardIdleTime,
    resumeTimer, stopTimer, pauseTimer
  } = useTimer();
  
  const [graceCountdown, setGraceCountdown] = useState(120); // 2 min grace before auto-stop
  const [wasAutoPaused, setWasAutoPaused] = useState(false);
  const graceIntervalRef = useRef(null);

  // When idle dialog appears, start grace countdown
  useEffect(() => {
    if (showIdleDialog && !wasAutoPaused) {
      setGraceCountdown(120);

      graceIntervalRef.current = setInterval(() => {
        setGraceCountdown(prev => {
          if (prev <= 1) {
            // Grace expired — auto-stop and deduct idle time
            handleAutoStop();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    if (!showIdleDialog) {
      setWasAutoPaused(false);
      if (graceIntervalRef.current) {
        clearInterval(graceIntervalRef.current);
        graceIntervalRef.current = null;
      }
    }

    return () => {
      if (graceIntervalRef.current) {
        clearInterval(graceIntervalRef.current);
      }
    };
  }, [showIdleDialog]);

  const handleAutoStop = useCallback(async () => {
    try {
      if (graceIntervalRef.current) {
        clearInterval(graceIntervalRef.current);
        graceIntervalRef.current = null;
      }
      const idleSecondsToDeduct = idleMinutes * 60;
      await stopTimer(idleSecondsToDeduct);
      toast.warning('Timer auto-stopped due to inactivity', {
        description: `${idleMinutes} minutes of idle time deducted from your entry`
      });
    } catch (error) {
      console.error('Auto-stop failed:', error);
    }
  }, [idleMinutes, stopTimer]);

  // Keep idle time — count it as work
  const handleKeepTime = () => {
    keepIdleTime();
    if (isPaused) resumeTimer();
    toast.success('Timer resumed', { description: 'All tracked time has been kept' });
  };

  // Discard idle time — subtract from elapsed
  const handleDiscardTime = () => {
    discardIdleTime(idleMinutes);
    if (isPaused) resumeTimer();
    toast.success('Idle time discarded', {
      description: `${idleMinutes} minutes removed from your time entry`
    });
  };

  // Keep as break
  const handleAddAsBreak = () => {
    keepIdleTime();
    toast.success('Break time kept', { description: 'Time kept and marked as break period' });
  };

  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!showIdleDialog) return null;

  const progressPercent = ((120 - graceCountdown) / 120) * 100;

  return (
    <AlertDialog open={showIdleDialog} onOpenChange={() => {}}>
      <AlertDialogContent className="sm:max-w-md rounded-2xl" data-testid="idle-alert-dialog">
        <AlertDialogHeader>
          <div className={`w-14 h-14 mx-auto mb-4 rounded-2xl flex items-center justify-center ${
            isPaused 
              ? 'bg-orange-100 dark:bg-orange-900/20' 
              : 'bg-amber-100 dark:bg-amber-900/20'
          }`}>
            {isPaused ? (
              <Pause className="w-7 h-7 text-orange-600 dark:text-orange-400" />
            ) : (
              <Clock className="w-7 h-7 text-amber-600 dark:text-amber-400" />
            )}
          </div>
          <AlertDialogTitle className="text-center text-xl">
            {isPaused ? 'Timer Paused' : 'Still working?'}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-center text-base">
            {isPaused ? (
              <>
                Your timer was <span className="font-semibold text-orange-600">auto-paused</span> after{' '}
                <span className="font-semibold text-slate-900 dark:text-white">{idleMinutes} minutes</span> of inactivity.
                <br />
                <span className="text-sm">What would you like to do with this idle time?</span>
              </>
            ) : (
              <>
                You've been away for <span className="font-semibold text-slate-900 dark:text-white">{idleMinutes} minutes</span> with no activity detected.
                <br />
                <span className="text-sm">Are you still tracking time?</span>
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        {/* Countdown progress */}
        <div className="py-3">
          <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
            <span className="flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Auto-stop in
            </span>
            <span className="font-mono font-medium text-amber-600">{formatCountdown(graceCountdown)}</span>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </div>
        
        <div className="grid gap-3 py-4">
          <Button
            onClick={handleKeepTime}
            className="h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
            data-testid="idle-keep-btn"
          >
            <Check className="w-4 h-4 mr-2" />
            {isPaused ? 'Resume & Keep all time' : "Yes, I'm working"}
          </Button>
          
          <Button
            onClick={handleAddAsBreak}
            variant="outline"
            className="h-12 rounded-xl font-semibold"
            data-testid="idle-break-btn"
          >
            <Coffee className="w-4 h-4 mr-2" />
            Keep as break time
          </Button>
          
          <Button
            onClick={handleDiscardTime}
            variant="outline"
            className="h-12 rounded-xl text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-50 dark:bg-red-900/10 font-semibold"
            data-testid="idle-discard-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Discard {idleMinutes} minutes
          </Button>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default IdleAlertDialog;
